import React, { useState } from 'react';
import {
  FaEdit, FaUser, FaEnvelope, FaPhone, FaLinkedin, FaGraduationCap,
  FaBriefcase, FaTrophy, FaCertificate, FaUsers, FaHashtag, FaMapMarkerAlt,
  FaDollarSign, FaCalendarAlt, FaVideo, FaBook, FaLanguage, FaInfoCircle, FaFileAlt
} from 'react-icons/fa';
import './Step10Review.css';
import ProfileEditModal from './ProfileEditModal';

// Helper to translate months
const translateMonth = (month, isArabic) => {
  if (!month) return '';
  if (!isArabic) return month;
  
  const monthMap = {
    'January': 'يناير',
    'February': 'فبراير',
    'March': 'مارس',
    'April': 'أبريل',
    'May': 'مايو',
    'June': 'يونيو',
    'July': 'يوليو',
    'August': 'أغسطس',
    'September': 'سبتمبر',
    'October': 'أكتوبر',
    'November': 'نوفمبر',
    'December': 'ديسمبر'
  };
  
  return monthMap[month] || month;
};

// Helper to format dates
const formatDate = (month, year, isArabic = false) => {
  if (!month || !year) return '';
  const translatedMonth = translateMonth(month, isArabic);
  return `${translatedMonth} ${year}`;
};

// Helper to translate grade levels
const translateGrade = (grade, isArabic) => {
  if (!grade) return '';
  const gradeStr = grade.toString();
  if (isArabic) {
    const gradeMatch = gradeStr.match(/Grade\s*(\d+)/i);
    if (gradeMatch) {
      return `الصف ${gradeMatch[1]}`;
    }
    const numMatch = gradeStr.match(/^(\d+)$/);
    if (numMatch) {
      return `الصف ${numMatch[1]}`;
    }
    const gradeMap = {
      'Foundation': 'سنة تأسيسية',
      'Diploma': 'دبلوم',
      'Bachelor': 'بكالوريوس',
      'Master': 'ماجستير',
      'PhD': 'دكتوراه'
    };
    return gradeMap[gradeStr] || gradeStr;
  }
  return gradeStr;
};

// Helper to translate subjects
const translateSubject = (subject, isArabic) => {
  if (!subject) return '';
  const subjects = {
    'Mathematics': 'الرياضيات',
    'Physics': 'الفيزياء',
    'Chemistry': 'الكيمياء',
    'Biology': 'الأحياء',
    'Science': 'العلوم',
    'English': 'الإنجليزية',
    'Arabic': 'العربية',
    'French': 'الفرنسية',
    'Spanish': 'الإسبانية',
    'History': 'التاريخ',
    'Geography': 'الجغرافيا',
    'Computer Science': 'علوم الحاسوب',
    'Programming': 'البرمجة',
    'Art': 'الفن',
    'Music': 'الموسيقى',
    'Physical Education': 'التربية البدنية',
    'Other': 'أخرى'
  };
  return isArabic ? (subjects[subject] || subject) : subject;
};

// Helper to translate languages
const translateLanguage = (language, isArabic) => {
  if (!language) return '';
  const languages = {
    'Arabic': 'العربية',
    'English': 'الإنجليزية',
    'French': 'الفرنسية',
    'Spanish': 'الإسبانية',
    'German': 'الألمانية',
    'Italian': 'الإيطالية',
    'Portuguese': 'البرتغالية',
    'Russian': 'الروسية',
    'Chinese': 'الصينية',
    'Japanese': 'اليابانية',
    'Korean': 'الكورية',
    'Hindi': 'الهندية',
    'Turkish': 'التركية'
  };
  return isArabic ? (languages[language] || language) : language;
};

// Helper to translate language levels
const translateLevel = (level, isArabic) => {
  if (!level) return '';
  const levels = {
    'Native': 'أصلي',
    'Fluent': 'طلاقة',
    'Advanced': 'متقدم',
    'Intermediate': 'متوسط',
    'Beginner': 'مبتدئ'
  };
  return isArabic ? (levels[level] || level) : level;
};

export default function Step10Review({ wizardData, onBack, onSubmit, isSubmitting, translations, isArabic, language, onDataUpdate, enableInlineEdit = false }) {
  const t = translations || {};

  // Modal state for inline editing
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [localWizardData, setLocalWizardData] = useState(wizardData);

  // Use local data if we have updates, otherwise use prop data
  const displayData = localWizardData || wizardData;

  const handleEdit = (step, section) => {
    // If inline edit is enabled, open modal instead of redirecting
    if (enableInlineEdit && section) {
      setEditSection(section);
      setEditModalOpen(true);
      return;
    }

    // Fallback to original redirect behavior
    if (window.location.pathname.includes('/teacher/signup')) {
      window.location.href = `/teacher/signup?step=${step}`;
    } else {
      window.location.href = `?step=${step}`;
    }
  };

  const handleModalClose = () => {
    setEditModalOpen(false);
    setEditSection(null);
  };

  const handleModalSave = async (editedData) => {
    setIsSaving(true);
    try {
      // Update local state immediately for UI feedback
      const updatedData = { ...displayData, ...editedData };
      setLocalWizardData(updatedData);

      // Call the parent's update handler if provided
      if (onDataUpdate) {
        await onDataUpdate(editedData);
      }

      handleModalClose();
    } catch (error) {
      console.error('Failed to save:', error);
      alert(isArabic ? 'فشل في الحفظ' : 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  const formatTime = (time24) => {
    if (!time24) return isArabic ? 'ص 9:00' : '9:00 AM';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours);
    const hour12 = hour % 12 || 12;
    const ampm = hour >= 12 
      ? (isArabic ? 'م' : 'PM') 
      : (isArabic ? 'ص' : 'AM');
    return isArabic 
      ? `${ampm} ${hour12}:${minutes}`
      : `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <div className="cv-review-container" dir={isArabic ? 'rtl' : 'ltr'}>
      {/* CV Header */}
      <div className="cv-header">
        <h2 className="cv-main-title">{t.reviewYourProfile || 'Review Your Profile'}</h2>
        <p className="cv-subtitle">
          {t.reviewSubtitle || 'Please review all your information before submitting. Click on any section to edit.'}
        </p>
      </div>

      {/* CV Layout - Two Columns */}
      <div className="cv-layout">
        {/* Left Column - Sidebar */}
        <div className="cv-sidebar">
          {/* Profile Photo */}
          <div className="cv-photo-section">
            {displayData.photo_url ? (
              <img src={displayData.photo_url} alt="Profile" className="cv-profile-photo" />
            ) : (
              <div className="cv-profile-photo-placeholder">
                <FaUser size={40} />
              </div>
            )}
            <h1 className="cv-name">{displayData.full_name || (isArabic ? 'اسمك' : 'Your Name')}</h1>
          </div>

          {/* Information Section */}
          <div className="cv-sidebar-section" onClick={() => handleEdit(1, 'information')}>
            <div className="cv-section-header">
              <FaInfoCircle className="cv-section-icon" />
              <h3 className="cv-section-title">{t.information || (isArabic ? 'المعلومات' : 'INFORMATION')}</h3>
            </div>
            <div className="cv-section-divider"></div>
            <div className="cv-contact-item">
              <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                {displayData.phone ? `+973 ${displayData.phone}` : '-'}
              </span>
            </div>
            <div className="cv-contact-item">
              <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                {displayData.email || '-'}
              </span>
            </div>
            {displayData.date_of_birth_day && displayData.date_of_birth_month && displayData.date_of_birth_year && (
              <div className="cv-contact-item">
                <span style={{ fontSize: '13px', opacity: 0.9 }}>
                  {displayData.date_of_birth_day} {displayData.date_of_birth_month} {displayData.date_of_birth_year}
                </span>
              </div>
            )}
            {displayData.gender && (
              <div className="cv-contact-item">
                <span style={{ fontSize: '13px', opacity: 0.9 }}>
                  {displayData.gender === 'Male' ? (isArabic ? 'ذكر' : 'Male') : displayData.gender === 'Female' ? (isArabic ? 'أنثى' : 'Female') : displayData.gender}
                </span>
              </div>
            )}
            </div>

          {/* About Me Section */}
          <div className="cv-sidebar-section" onClick={() => handleEdit(4, 'aboutMe')}>
            <div className="cv-section-header">
              <FaUser className="cv-section-icon" />
              <h3 className="cv-section-title">{t.aboutMe || (isArabic ? 'نبذة عني' : 'ABOUT ME')}</h3>
            </div>
            <div className="cv-section-divider"></div>
            <p className="cv-about-text">
              {displayData.short_bio || (isArabic ? 'لا توجد نبذة' : 'No bio provided')}
            </p>
          </div>

          {/* Grade Levels Section */}
          <div className="cv-sidebar-section" onClick={() => handleEdit(1, 'gradeLevels')}>
            <div className="cv-section-header">
              <FaGraduationCap className="cv-section-icon" />
              <h3 className="cv-section-title">{t.gradeLevels || (isArabic ? 'المستويات' : 'GRADE LEVELS')}</h3>
            </div>
            <div className="cv-section-divider"></div>
            <ul className="cv-skills-list">
              {displayData.grade_levels && displayData.grade_levels.length > 0 ? (
                displayData.grade_levels.map((grade, idx) => (
                  <li key={idx}>{translateGrade(grade, isArabic)}</li>
                ))
              ) : (
                <li>{isArabic ? 'لم يتم الاختيار' : 'Not selected'}</li>
              )}
            </ul>
          </div>

          {/* Languages Section */}
          <div className="cv-sidebar-section" onClick={() => handleEdit(1, 'languages')}>
            <div className="cv-section-header">
              <FaLanguage className="cv-section-icon" />
              <h3 className="cv-section-title">{t.languages || (isArabic ? 'اللغات' : 'LANGUAGES')}</h3>
            </div>
            <div className="cv-section-divider"></div>
            <ul className="cv-skills-list">
              {displayData.languages && displayData.languages.length > 0 ? (
                displayData.languages.map((lang, idx) => {
                  // Handle both string format and object format
                  const languageName = typeof lang === 'string' ? lang : (lang.language || '');
                  const level = typeof lang === 'object' ? lang.level : '';

                  return (
                    <li key={idx}>
                      {translateLanguage(languageName, isArabic)}
                      {level && ` (${translateLevel(level, isArabic)})`}
                    </li>
                  );
                })
              ) : (
                <li>{isArabic ? 'لم يتم الاختيار' : 'Not selected'}</li>
              )}
            </ul>
          </div>

          {/* Video Section - Only if video is available */}
          {displayData.intro_video_url && (
            <div className="cv-sidebar-section" onClick={() => handleEdit(5, 'video')}>
              <div className="cv-section-header">
                <FaVideo className="cv-section-icon" />
                <h3 className="cv-section-title">{t.video || (isArabic ? 'الفيديو' : 'VIDEO')}</h3>
              </div>
              <div className="cv-section-divider"></div>
              <div className="cv-video-preview">
                {displayData.intro_video_url.includes('youtube.com') || displayData.intro_video_url.includes('youtu.be') ? (
                  <iframe
                    src={displayData.intro_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title="Intro Video"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="cv-video-iframe"
                  />
                ) : displayData.intro_video_url.includes('vimeo.com') ? (
                  <iframe
                    src={displayData.intro_video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    title="Intro Video"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen
                    className="cv-video-iframe"
                  />
                ) : (
                  <video controls src={displayData.intro_video_url} className="cv-video-element" />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Main Content */}
        <div className="cv-main-content">
          {/* Subjects Section */}
          <div className="cv-content-section" onClick={() => handleEdit(1, 'subjects')}>
            <div className="cv-content-header">
              <FaBook className="cv-content-icon" />
              <h3 className="cv-content-title">{isArabic ? 'المواد' : 'SUBJECTS'}</h3>
            </div>
            <div className="cv-content-divider"></div>
            {(displayData.subjects && displayData.subjects.length > 0) || displayData.subject ? (
              <ul className="cv-bullet-list">
                {displayData.subjects && displayData.subjects.length > 0 ? (
                  displayData.subjects.map((subj, idx) => {
                    const types = displayData.subject_types?.[subj] || [];
                    return (
                      <li key={idx} style={{ marginBottom: types.length > 0 ? '12px' : '8px' }}>
                        <strong>{translateSubject(subj, isArabic)}</strong>
                        {types.length > 0 && (
                          <div style={{
                            marginTop: '6px',
                            paddingLeft: '16px',
                            fontSize: '13px',
                            color: '#6b7280'
                          }}>
                            {types.join(' • ')}
                          </div>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <li>
                    <strong>{translateSubject(displayData.subject, isArabic)}</strong>
                  </li>
                )}
              </ul>
            ) : (
              <p className="cv-empty-text">{isArabic ? 'لم يتم اختيار مادة' : 'No subject selected'}</p>
            )}
          </div>

          {/* Experience Section */}
          <div className="cv-content-section" onClick={() => handleEdit(3, 'experience')}>
            <div className="cv-content-header">
              <FaBriefcase className="cv-content-icon" />
              <h3 className="cv-content-title">{t.experience || (isArabic ? 'الخبرة' : 'EXPERIENCE')}</h3>
            </div>
            <div className="cv-content-divider"></div>
            {displayData.experience_none ? (
              <p className="cv-empty-text">{t.noExperience || 'I don\'t have any experience'}</p>
            ) : displayData.experiences && displayData.experiences.length > 0 ? (
              <div className="cv-timeline">
                {(() => {
                  // Sort experiences: present experiences go to the bottom
                  const sortedExperiences = [...displayData.experiences].sort((a, b) => {
                    // If one is present and the other is not, present goes to bottom
                    if (a.present && !b.present) return 1;
                    if (!a.present && b.present) return -1;
                    // If both are present or both are not, maintain original order
                    return 0;
                  });

                  return sortedExperiences.map((exp, idx) => (
                    <div key={idx} className="cv-timeline-item">
                      <div className="cv-timeline-bullet"></div>
                      {idx < sortedExperiences.length - 1 && <div className="cv-timeline-line"></div>}
                      <div className="cv-timeline-content">
                        <div className="cv-timeline-org">{exp.organization || '-'}</div>
                        <div className="cv-timeline-title-italic">{exp.job_title || '-'}</div>
                        <div className="cv-timeline-date">
                          {formatDate(exp.from_month, exp.from_year, isArabic)} - {exp.present
                            ? (isArabic ? 'حالياً' : 'Present')
                            : formatDate(exp.to_month, exp.to_year, isArabic)}
                        </div>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            ) : (
              <p className="cv-empty-text">{isArabic ? 'لا توجد خبرة' : 'No experience added'}</p>
            )}
          </div>

          {/* Qualifications Section */}
          <div className="cv-content-section" style={{ cursor: 'default' }}>
            <div className="cv-content-header">
              <FaCertificate className="cv-content-icon" />
              <h3 className="cv-content-title">{isArabic ? 'المؤهلات (اختياري ولكن يُنصح به)' : 'QUALIFICATIONS (Optional but Recommended)'}</h3>
            </div>
            <div className="cv-content-divider"></div>
            {displayData.qualifications && displayData.qualifications.length > 0 ? (
              <ul className="cv-bullet-list">
                {displayData.qualifications.map((qual, idx) => (
                  <li key={idx}>
                    <a
                      href={qual.url || qual}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#3b82f6',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      <FaFileAlt size={14} />
                      {isArabic ? `شهادة ${idx + 1}` : `Certificate ${idx + 1}`}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="cv-empty-text">
                {isArabic
                  ? 'لم يتم رفع مؤهلات. يُنصح بشدة برفع الشهادات أو الدرجات لبناء الثقة مع الطلاب.'
                  : 'No qualifications uploaded. Highly recommended to upload certificates or degrees to build trust with students.'}
              </p>
            )}
          </div>

          {/* Pricing Section */}
          <div className="cv-content-section" onClick={() => handleEdit(8, 'pricing')}>
            <div className="cv-content-header">
              <FaDollarSign className="cv-content-icon" />
              <h3 className="cv-content-title">{t.pricing || (isArabic ? 'التسعير' : 'PRICING')}</h3>
            </div>
            <div className="cv-content-divider"></div>
            {displayData.lesson_format_preference === 'both' ? (
              <ul className="cv-bullet-list">
                <li style={{ marginBottom: '16px' }}>
                  <strong>{isArabic ? 'التسعير (أونلاين)' : 'Online Pricing'}</strong>
                </li>
                <li style={{ marginLeft: '20px' }}>
                  <strong>{t.perHourPrivate || 'Per hour (private)'}:</strong>{' '}
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {displayData.pricing_private_online_per_hour
                      ? (displayData.pricing_private_online_per_hour.toString().includes('BD')
                          ? displayData.pricing_private_online_per_hour
                          : `${displayData.pricing_private_online_per_hour} BD`)
                      : '-'}
                  </span>
                </li>
                <li style={{ marginLeft: '20px' }}>
                  <strong>{t.perHourGroup || 'Per hour (group)'}:</strong>{' '}
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {displayData.pricing_group_online_per_hour
                      ? (displayData.pricing_group_online_per_hour.toString().includes('BD')
                          ? displayData.pricing_group_online_per_hour
                          : `${displayData.pricing_group_online_per_hour} BD`)
                      : '-'}
                  </span>
                  {displayData.group_pricing_model_online && (
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                      ({displayData.group_pricing_model_online === 'per_person'
                        ? (isArabic ? 'لكل طالب' : 'Per Student')
                        : (isArabic ? 'سعر ثابت' : 'Fixed Price')})
                    </span>
                  )}
                </li>
                <li style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <strong>{isArabic ? 'التسعير (حضوري)' : 'Face-to-Face Pricing'}</strong>
                </li>
                <li style={{ marginLeft: '20px' }}>
                  <strong>{t.perHourPrivate || 'Per hour (private)'}:</strong>{' '}
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {displayData.pricing_private_face_to_face_per_hour
                      ? (displayData.pricing_private_face_to_face_per_hour.toString().includes('BD')
                          ? displayData.pricing_private_face_to_face_per_hour
                          : `${displayData.pricing_private_face_to_face_per_hour} BD`)
                      : '-'}
                  </span>
                </li>
                <li style={{ marginLeft: '20px' }}>
                  <strong>{t.perHourGroup || 'Per hour (group)'}:</strong>{' '}
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {displayData.pricing_group_face_to_face_per_hour
                      ? (displayData.pricing_group_face_to_face_per_hour.toString().includes('BD')
                          ? displayData.pricing_group_face_to_face_per_hour
                          : `${displayData.pricing_group_face_to_face_per_hour} BD`)
                      : '-'}
                  </span>
                  {displayData.group_pricing_model_face_to_face && (
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                      ({displayData.group_pricing_model_face_to_face === 'per_person'
                        ? (isArabic ? 'لكل طالب' : 'Per Student')
                        : (isArabic ? 'سعر ثابت' : 'Fixed Price')})
                    </span>
                  )}
                </li>
              </ul>
            ) : (
              <ul className="cv-bullet-list">
                <li>
                  <strong>{t.perHourPrivate || 'Per hour (private)'}:</strong>{' '}
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {displayData.pricing_private_per_hour
                      ? (displayData.pricing_private_per_hour.toString().includes('BHD') || displayData.pricing_private_per_hour.toString().includes('BD')
                          ? displayData.pricing_private_per_hour
                          : `${displayData.pricing_private_per_hour} BD`)
                      : '-'}
                  </span>
                </li>
                <li>
                  <strong>{t.perHourGroup || 'Per hour (group)'}:</strong>{' '}
                  <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                    {displayData.pricing_group_per_hour
                      ? (displayData.pricing_group_per_hour.toString().includes('BHD') || displayData.pricing_group_per_hour.toString().includes('BD')
                          ? displayData.pricing_group_per_hour
                          : `${displayData.pricing_group_per_hour} BD`)
                      : '-'}
                  </span>
                  {displayData.group_pricing_model && (
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '8px' }}>
                      ({displayData.group_pricing_model === 'per_person'
                        ? (isArabic ? 'لكل طالب' : 'Per Student')
                        : (isArabic ? 'سعر ثابت' : 'Fixed Price')})
                    </span>
                  )}
                </li>
              </ul>
            )}
          </div>

          {/* Banking Information Section */}
          {(displayData.payment_method || displayData.iban || displayData.bank_name || displayData.account_holder_name) && (
            <div className="cv-content-section" onClick={() => handleEdit(8, 'banking')}>
              <div className="cv-content-header">
                <FaDollarSign className="cv-content-icon" />
                <h3 className="cv-content-title">{t.bankingInformation || (isArabic ? 'معلومات الدفع' : 'PAYMENT INFORMATION')}</h3>
              </div>
              <div className="cv-content-divider"></div>
              <ul className="cv-bullet-list">
                {displayData.payment_method && (
                  <li>
                    <strong>{isArabic ? 'طريقة الدفع:' : 'Payment Method:'}</strong>{' '}
                    {displayData.payment_method === 'benefitpay'
                      ? (isArabic ? 'بنفت باي' : 'BenefitPay')
                      : (isArabic ? 'تحويل بنكي' : 'Bank Transfer')}
                  </li>
                )}
                {displayData.payment_method === 'benefitpay' && displayData.phone && (
                  <li>
                    <strong>{isArabic ? 'رقم بنفت باي:' : 'BenefitPay Number:'}</strong>{' '}
                    <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                      +973 {displayData.phone}
                    </span>
                  </li>
                )}
                {displayData.iban && (
                  <li>
                    <strong>IBAN:</strong>{' '}
                    <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'isolate' }}>
                      {displayData.iban}
                    </span>
                  </li>
                )}
                {displayData.bank_name && (
                  <li>
                    <strong>{isArabic ? 'اسم البنك:' : 'Bank Name:'}</strong> {displayData.bank_name}
                  </li>
                )}
                {displayData.account_holder_name && (
                  <li>
                    <strong>{isArabic ? 'اسم صاحب الحساب:' : 'Account Holder Name:'}</strong> {displayData.account_holder_name}
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Availability Section */}
          <div className="cv-content-section" onClick={() => handleEdit(6, 'availability')}>
            <div className="cv-content-header">
              <FaCalendarAlt className="cv-content-icon" />
              <h3 className="cv-content-title">{t.availability || (isArabic ? 'التوفر' : 'AVAILABILITY')}</h3>
            </div>
            <div className="cv-content-divider"></div>
            {displayData.availability_schedule ? (
              <ul className="cv-bullet-list">
                {['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].map(day => {
                  const dayData = displayData.availability_schedule[day];
                  if (!dayData || dayData.notAvailable) return null;
                  const dayLabels = {
                    EN: { sunday: 'Sunday', monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday' },
                    AR: { sunday: 'الأحد', monday: 'الإثنين', tuesday: 'الثلاثاء', wednesday: 'الأربعاء', thursday: 'الخميس', friday: 'الجمعة', saturday: 'السبت' }
                  };
                  const dayLabel = dayLabels[language]?.[day] || day;
                  const slots = dayData.slots || [];
                  if (dayData.allDay) {
                    return (
                      <li key={day}>
                        <strong>{dayLabel}:</strong> {isArabic ? 'طوال اليوم' : 'All Day'}
                      </li>
                    );
                  }
                  if (slots.length === 0) return null;
                  return (
                    <li key={day}>
                      <strong>{dayLabel}:</strong> {slots.map(slot => `${formatTime(slot.from)} - ${formatTime(slot.to)}`).join(', ')}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="cv-empty-text">{isArabic ? 'لم يتم تحديد التوفر' : 'No availability set'}</p>
            )}
          </div>

          {/* Places Section */}
          <div className="cv-content-section" onClick={() => handleEdit(7, 'teachingLocations')}>
            <div className="cv-content-header">
              <FaMapMarkerAlt className="cv-content-icon" />
              <h3 className="cv-content-title">{t.teachingLocations || (isArabic ? 'مواقع التدريس' : 'TEACHING LOCATIONS')}</h3>
            </div>
            <div className="cv-content-divider"></div>
            <div style={{ marginBottom: '12px' }}>
              <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#1f2937' }}>
                {t.howDeliverLessons || 'How do you prefer to deliver lessons?'}
              </strong>
              <div style={{ fontSize: '14px', color: '#374151', marginLeft: '0' }}>
                {displayData.lesson_format_preference === 'online_only'
                  ? (t.onlineOnly || 'Online Only')
                  : displayData.lesson_format_preference === 'face_to_face_only'
                  ? (t.faceToFaceOnly || 'Face-to-Face Only')
                  : displayData.lesson_format_preference === 'both'
                  ? (isArabic ? 'وجهاً لوجه وأونلاين' : 'Face to Face and Online')
                  : '-'}
              </div>
            </div>
            {displayData.teacher_locations && displayData.teacher_locations.length > 0 && (
              <div style={{ marginTop: '16px' }}>
                <strong style={{ display: 'block', marginBottom: '8px', fontSize: '14px', color: '#1f2937' }}>
                  {isArabic ? 'المواقع المفضلة:' : 'Preferred Locations:'}
                </strong>
                <ul className="cv-bullet-list" style={{ marginTop: '8px' }}>
                  {displayData.teacher_locations.map((loc, idx) => (
                    <li key={idx} style={{ marginBottom: '4px' }}>
                      {loc.formatted_address || loc.address || loc.place_name || (isArabic ? `${t.location || 'موقع'} ${idx + 1}` : `${t.location || 'Location'} ${idx + 1}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions - Only show if onSubmit is provided (not in admin view) */}
      {onSubmit && (
        <div className="cv-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => handleEdit(8)}
            disabled={isSubmitting}
          >
            {t.back || 'Back'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting
              ? (isArabic ? 'جاري الإرسال...' : 'Submitting...')
              : (t.submitForReview || 'Submit for Review')}
          </button>
        </div>
      )}

      {/* Edit Modal for inline editing */}
      {enableInlineEdit && (
        <ProfileEditModal
          open={editModalOpen}
          onClose={handleModalClose}
          section={editSection}
          wizardData={displayData}
          onSave={handleModalSave}
          isArabic={isArabic}
          language={language}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
